import {
  apiAddUserAssignmentInfo,
  apiAssignedUsersWeb,
  apiCandidateUsersSearchWeb,
  apiCandidateUsersWeb,
  apiDeleteUserAssignmentInfo,
  apiDenyUserAssignmentInfo,
  apiGetRoleTemplatePermissions,
  apiGetUserAssignmentInfo,
  apiGroupDetail,
  apiPendingUsersWeb,
  apiUpdateUserAssignmentInfo,
  getUser,
} from "@app/api";
import { DataDialog, DataTable } from "@app/components/atoms";
import {
  IconActionOverflow,
  IconBox,
  IconDelete,
  IconEdit,
  IconInvalidUrl,
  IconPlus,
  IconUser,
} from "@app/components/icons";
import {
  CURRENT_PAGE,
  INVALID_URL,
  LIMIT_PAGE,
  TOTAL_PAGE,
} from "@app/constants";
import { isMobile, resetIsContinuousLoading } from "@app/helpers";
import {
  RootState,
  saveScreenPermission,
  setIsRequestedGroupDetail,
  setShowAlert,
  store,
} from "@app/store";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { InputText } from "primereact/inputtext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useOutletContext, useParams } from "react-router-dom";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Divider, InputAdornment } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { QRCodeCanvas } from "qrcode.react";
import { DownloadIcon } from "lucide-react";

const ALWAYS_DISABLED = [
  "065BDR92ZNGHX3R18CANF69Z00",
  "065BDR92ZSK8KXMJZBQKKS9YFX",
];
type pendingUsers = {
  memberGroupInvitationId: string;
  groupId: string;
  inviterUserId: string;
  invitationStatus: string;
  userId: string;
  searchId: string;
  reporterName: string;
  pictureUrl: string;
  email: string;
  isSearchableByEmail: boolean;
};

type assignedUsers = {
  groupId: string;
  userId: string;
  searchId: string;
  reporterName: string;
  pictureUrl: string;
  email: string;
  isSearchableByEmail: boolean;
  roleName: string;
  isCurrentUser: boolean;
};

type roleTemplates = {
  userGroupRoleTemplateId: string;
  roleCode: string;
  userGroupRoleNameTranslation: {
    userGroupRoleTemplateId: string;
    isoLocaleCode: string;
    roleName: string;
  };
  permissionTranslates: {
    permissionId: string;
    permissionName: string;
    isChecked: boolean;
  }[];
};

type permissions = {
  permissionId: string;
  permissionName: string;
  isChecked: boolean;
  disabled?: boolean;
};

type CandidateUser = {
  userId: string;
  surname: string;
  givenName: string;
  email: string;
  pictureUrl: string | null;
  searchId: string;
};

type CandidateSearchUser = {
  userId: string;
  surname: string;
  givenName: string;
  email: string;
  pictureUrl: string | null;
  searchId: string;
  isAlreadyInGroup: boolean;
};

type groupType = {
  groupId: string;
  groupName: string;
  companyName: string;
  machineCount: number;
  reportCount: number;
  location: string;
  memberCount: number;
  inspectionFormCount: number;
};

export function MemberTab() {
  const { t } = useTranslation();
  const { id } = useParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [field, setField] = useState<string>("");
  const [order, setOrder] = useState<string | undefined>();
  const [assignedField, setAssignedField] = useState<string>("");
  const [assignedOrder, setAssignedOrder] = useState<string | undefined>();
  const [rows, setRow] = useState<pendingUsers[]>();
  const [assignedRows, setAssignedRows] = useState<assignedUsers[]>();
  const [pageInfo, setPageInfo] = useState({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });
  const [assignedPageInfo, setAssignedPageInfo] = useState({
    total: TOTAL_PAGE,
    page: CURRENT_PAGE,
    limit: LIMIT_PAGE,
  });
  const [page, setPage] = useState(CURRENT_PAGE);
  const [assignedPage, setAssignedPage] = useState(CURRENT_PAGE);
  const [searchText, setSearchText] = useState("");
  const [searchTextMember, setSearchTextMember] = useState("");
  const [assignedSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState(searchText);
  const [assignedAppliedSearch, setAssignedAppliedSearch] =
    useState(assignedSearchText);
  const { setMemberSearch, setPendingApprovals } = useOutletContext<{
    setMemberSearch: (val: string) => void;
    setPendingApprovals: (count: number) => void;
  }>();
  const [anchorElAssigned, setAnchorElAssigned] = useState<null | HTMLElement>(
    null,
  );
  const openAssigned = Boolean(anchorElAssigned);
  const [openUpdateMember, setOpenUpdateMember] = useState(false);
  const [openAddPendingMember, setOpenAddPendingMember] = useState(false);
  const [openAddNewMember, setOpenAddNewMember] = useState(false);
  const [openAddNewMemberSearch, setOpenAddNewMemberSearch] = useState(false);
  const [openAddMember, setOpenAddMember] = useState(false);
  const [openAddSearchMember, setOpenAddSearchMember] = useState(false);
  const [openDeleteMember, setOpenDeleteMember] = useState(false);
  const [openDenyMember, setOpenDenyMember] = useState(false);
  const [allowCreateEditDeleteMember, setAllowCreateEditDeleteMember] =
    useState<boolean>(false);
  const [targetRow, setTargetRow] = useState<any | null>(null);
  const [templates, setTemplates] = useState<roleTemplates[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [permissions, setPermissions] = useState<permissions[]>([]);
  const [extraRoleName, setExtraRoleName] = useState("");
  const [targetPendingRow, setTargetPendingRow] = useState<pendingUsers | null>(
    null,
  );
  const [addMode] = useState<"search" | "qrcode" | "link">("search");
  const [memberQuery, setMemberQuery] = useState("");
  const [candidateAll, setCandidateAll] = useState<CandidateUser[]>([]);
  const [candidateSearchAll, setCandidateSearchAll] = useState<
    CandidateSearchUser[]
  >([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateSearchLoading, setCandidateSearchLoading] = useState(false);
  const [localeCode, setLocaleCode] = useState("");
  const [userID, setUserID] = useState("");
  const [targetCandidateRow, setTargetCandidateRow] =
    useState<CandidateUser | null>(null);
  const [targetCandidateSearchRow, setTargetCandidateSearchRow] =
    useState<CandidateSearchUser | null>(null);
  const [titleDetail, setTitleDetail] = useState<groupType>();
  const dispatch = useDispatch();
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [open2DCodeDialog, setOpen2DCodeDialog] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [userTimeFormat, setUserTimeFormat] = useState("dd/MM/yyyy");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(false);

  const pendingUserColumns = [
    {
      key: "pictureUrl",
      title: "",
      dataIndex: "pictureUrl",
      render: (_: any, record: pendingUsers) => {
        return (
          <>
            {!!record.pictureUrl ? (
              record.pictureUrl === INVALID_URL ? (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconInvalidUrl />
                </Box>
              ) : (
                <Avatar
                  sx={{ width: 24, height: 24 }}
                  src={record.pictureUrl}
                  imgProps={{
                    onError: () => {
                      const newRows = rows?.map((item) => {
                        if (item.userId === record.userId) {
                          item.pictureUrl = INVALID_URL;
                        }
                        return item;
                      });
                      setRow(newRows);
                    },
                  }}
                />
              )
            ) : (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconUser />
              </Box>
            )}
          </>
        );
      },
      forceRender: true,
      className: "pr-4",
      tableHeadingClassName: "d-none-imp",
      width: 40,
    },
    {
      key: "reporterName",
      title: "",
      dataIndex: "reporterName",
      sorted: false,
      tableHeadingClassName: "d-none-imp",
      className: "pl-0 txt-pre-wrap",
      style: {
        minWidth: isMobile() ? 120 : 185,
      },
      render: (value: any, record: any) => {
        return (
          <Link
            to={`/groups/${id}/members`}
            onClick={() => {
              setTargetPendingRow(record);
              handleOpenAdd();
            }}
            style={{
              paddingLeft: 0,
              textDecoration: "underline",
              color: "#140a9a",
              fontWeight: 500,
            }}
          >
            {value}
          </Link>
        );
      },
    },
  ];

  const pendingColumns = [...pendingUserColumns];

  const assignedUserColumns = [
    {
      key: "pictureUrl",
      title: "",
      dataIndex: "pictureUrl",
      render: (_: any, record: assignedUsers) => {
        return (
          <>
            {!!record.pictureUrl ? (
              record.pictureUrl === INVALID_URL ? (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconInvalidUrl />
                </Box>
              ) : (
                <Avatar
                  sx={{ width: 24, height: 24 }}
                  src={record.pictureUrl}
                  imgProps={{
                    onError: () => {
                      const newRows = assignedRows?.map((item) => {
                        if (item.userId === record.userId) {
                          item.pictureUrl = INVALID_URL;
                        }
                        return item;
                      });
                      setAssignedRows(newRows);
                    },
                  }}
                />
              )
            ) : (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconUser />
              </Box>
            )}
          </>
        );
      },
      forceRender: true,
      className: "pr-4",
      tableHeadingClassName: "d-none-imp",
      width: 40,
    },
    {
      key: "reporterName",
      title: "username",
      dataIndex: "reporterName",
      sorted: true,
      tableHeadingColspan: 2,
      className: "pl-0 txt-pre-wrap",
      style: {
        minWidth: isMobile() ? 120 : 185,
      },
    },
    {
      key: "roleName",
      title: "role",
      align: "left",
      dataIndex: "roleName",
      sorted: true,
      className: "pl-0 txt-pre-wrap",
      style: {
        minWidth: isMobile() ? 120 : 185,
      },
    },
    {
      key: "userId",
      title: "",
      dataIndex: "userId",
      render: (_: string, record: assignedUsers) => {
        if (record.isCurrentUser) {
          return null;
        }

        return (
          <Button
            className="bnt-action-form"
            onClick={(e) => handleClick(e, record)}
          >
            <IconActionOverflow />
          </Button>
        );
      },
      className: "add",
      width: 60,
      align: "center",
    },
  ];

  const assignedColumns = [...assignedUserColumns];

  const handleOpenAdd = async () => {
    fetchTemplates();
    setOpenAddPendingMember(true);
  };

  const handleClick = async (
    event: React.MouseEvent<HTMLElement>,
    target: any,
  ) => {
    setAnchorElAssigned(event.currentTarget);
    setTargetRow(target);

    try {
    } catch (error) {
      console.error("Failed to fetch user assignment info:", error);
    }
  };

  const handleSort = (order: string | undefined, property: string) => {
    setField(property);
    setOrder(order);
  };

  const handleAssignedSort = (
    assignedOrder: string | undefined,
    assignedProperty: string,
  ) => {
    setAssignedField(assignedProperty);
    setAssignedOrder(assignedOrder);
  };

  const screenPermission = useSelector(
    (state: RootState) => state?.screenPermission.value,
  );

  const clearFields = () => {
    setSelectedRole("");
    setExtraRoleName("");
    setPermissions([]);
    setLoadingTemplates(false);
    setTemplates([]);
    setOpenUpdateMember(false);
    setTargetRow(null);
    setTargetPendingRow(null);
    setOpenAddPendingMember(false);
    setOpenAddMember(false);
    setOpenAddNewMember(false);
    setTargetCandidateRow(null);
    setMemberQuery("");
    setCandidateAll([]);
    setCandidateSearchAll([]);
    setOpenAddSearchMember(false);
    setTargetCandidateSearchRow(null);
    setSearchTextMember("");
    setCandidateSearchLoading(false);
    setCandidateLoading(false);
    setSearchText("");
    setOpenAddNewMemberSearch(false);
  };

  const clearFieldsAddMember = () => {
    setSelectedRole("");
    setExtraRoleName("");
    setPermissions([]);
    setLoadingTemplates(false);
    setTemplates([]);
    setOpenUpdateMember(false);
    setTargetRow(null);
    setTargetPendingRow(null);
    setOpenAddPendingMember(false);

    setOpenAddNewMember(false);
    setTargetCandidateRow(null);
    setMemberQuery("");

    setCandidateSearchAll([]);
    setOpenAddSearchMember(false);
    setTargetCandidateSearchRow(null);
    setSearchTextMember("");
    setCandidateSearchLoading(false);
    setCandidateLoading(false);
    setSearchText("");
    setOpenAddNewMemberSearch(false);
  };

  const clearFieldsAddMemberSearch = () => {
    setSelectedRole("");
    setExtraRoleName("");
    setPermissions([]);
    setLoadingTemplates(false);
    setTemplates([]);
    setOpenUpdateMember(false);
    setTargetRow(null);
    setTargetPendingRow(null);
    setOpenAddPendingMember(false);

    setOpenAddNewMember(false);
    setTargetCandidateRow(null);
    setMemberQuery("");

    setTargetCandidateSearchRow(null);
    setSearchTextMember("");
    setCandidateSearchLoading(false);
    setCandidateLoading(false);
    setSearchText("");
    setOpenAddNewMemberSearch(false);
  };

  const handleOpenEdit = async () => {
    await fetchTemplates(false);
    await fetchUserAssignmentInfo(id ?? "", targetRow.userId);
    setOpenUpdateMember(true);
  };

  const applyRolePermissions = (role: roleTemplates): permissions[] => {
    let perms = role.permissionTranslates;

    if (role.roleCode === "role code 1") {
      return perms.map((p) => ({
        ...p,
        isChecked: true,
        disabled: true,
      }));
    } else {
      return perms.map((p) => ({
        ...p,
        disabled: ALWAYS_DISABLED.includes(p.permissionId),
      }));
    }
  };

  const handleRoleChange = (roleId: string) => {
    setSelectedRole(roleId);
    setExtraRoleName("");

    const role = templates.find((r) => r.userGroupRoleTemplateId === roleId);
    if (role) {
      setPermissions(applyRolePermissions(role));
    }
  };

  const fetchTemplates = useCallback(async (resetSelection = true) => {
    try {
      setLoadingTemplates(true);
      const res = await apiGetRoleTemplatePermissions();
      setTemplates(res.data.data || []);

      if (resetSelection && res.data.data?.length) {
        const allPermsMap = new Map();

        res.data.data.forEach((role: { permissionTranslates: any[] }) => {
          role.permissionTranslates.forEach((p) => {
            allPermsMap.set(p.permissionId, {
              ...p,
              isChecked: false,
              disabled: ALWAYS_DISABLED.includes(p.permissionId),
            });
          });
        });

        setSelectedRole("");
        setPermissions(Array.from(allPermsMap.values()));
      }
    } catch (err) {
      console.error("Failed to load role templates:", err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const togglePermission = (permId: string) => {
    setPermissions((prev) =>
      prev.map((p) =>
        p.permissionId === permId && !p.disabled
          ? { ...p, isChecked: !p.isChecked }
          : p,
      ),
    );
  };

  const fetchUserAssignmentInfo = useCallback(
    async (groupId: string, userId: string) => {
      try {
        const res = await apiGetUserAssignmentInfo(groupId, userId);
        const data = res.data.data;

        setSelectedRole(data.userGroupRoleTemplateId);

        if (data.roleName) {
          setExtraRoleName(data.roleName);
        } else {
          setExtraRoleName("");
        }

        setPermissions(
          data.permissions.map((p: any) => ({
            ...p,
            disabled: ALWAYS_DISABLED.includes(p.permissionId),
          })),
        );
      } catch (err) {
        console.error("Failed to load user assignment info:", err);
      }
    },
    [],
  );

  const handleUpdateUserAssignmentInfo = async () => {
    try {
      const permissionIds = permissions
        .filter((p) => p.isChecked)
        .map((p) => p.permissionId);

      const selectedTemplate = templates.find(
        (r) => r.userGroupRoleTemplateId === selectedRole,
      );

      const payload = {
        userGroupRoleName: extraRoleName || "",
        isAdmin: selectedTemplate?.roleCode === "role code 1",
        userGroupRoleTemplateId: selectedRole,
        permissionIds,
      };

      await apiUpdateUserAssignmentInfo(id ?? "", targetRow.userId, payload);
      store.dispatch(setShowAlert(t("memberUpdateSuccess")));
      await fetchAssignedUsers();
      await fetchPendingUsers();
      clearFields();
      setOpenUpdateMember(false);
    } catch (error) {
      console.error("Failed to update user assignment:", error);
    } finally {
    }
  };

  const handleAddPendingUserAssignmentInfo = async () => {
    try {
      const permissionIds = permissions
        .filter((p) => p.isChecked)
        .map((p) => p.permissionId);

      const selectedTemplate = templates.find(
        (r) => r.userGroupRoleTemplateId === selectedRole,
      );

      const payload = {
        inviteeUserId: targetPendingRow?.userId,
        userGroupRoleName: extraRoleName || "",
        isAdmin: selectedTemplate?.roleCode === "role code 1",
        userGroupRoleTemplateId: selectedRole,
        permissionIds,
      };

      await apiAddUserAssignmentInfo(id ?? "", payload);
      store.dispatch(setShowAlert(t("memberAddSuccess")));
      await fetchAssignedUsers();
      await fetchPendingUsers();
      clearFields();

      setOpenAddPendingMember(false);
    } catch (error) {
      console.error("Failed to add user assignment:", error);
    } finally {
    }
  };

  const handleAddUserAssignmentInfo = async () => {
    try {
      const permissionIds = permissions
        .filter((p) => p.isChecked)
        .map((p) => p.permissionId);

      const selectedTemplate = templates.find(
        (r) => r.userGroupRoleTemplateId === selectedRole,
      );

      const payload = {
        inviteeUserId: targetCandidateRow?.userId,
        userGroupRoleName: extraRoleName || "",
        isAdmin: selectedTemplate?.roleCode === "role code 1",
        userGroupRoleTemplateId: selectedRole,
        permissionIds,
      };

      await apiAddUserAssignmentInfo(id ?? "", payload);
      store.dispatch(setShowAlert(t("memberAddSuccess")));
      await fetchAssignedUsers();
      await fetchPendingUsers();
      clearFields();

      setOpenAddPendingMember(false);
    } catch (error) {
      console.error("Failed to add user assignment:", error);
    } finally {
    }
  };

  const handleAddSearchUserAssignmentInfo = async () => {
    try {
      const permissionIds = permissions
        .filter((p) => p.isChecked)
        .map((p) => p.permissionId);

      const selectedTemplate = templates.find(
        (r) => r.userGroupRoleTemplateId === selectedRole,
      );

      const payload = {
        inviteeUserId: targetCandidateSearchRow?.userId,
        userGroupRoleName: extraRoleName || "",
        isAdmin: selectedTemplate?.roleCode === "role code 1",
        userGroupRoleTemplateId: selectedRole,
        permissionIds,
      };

      await apiAddUserAssignmentInfo(id ?? "", payload);
      store.dispatch(setShowAlert(t("memberAddSuccess")));
      await fetchAssignedUsers();
      await fetchPendingUsers();
      clearFields();

      setOpenAddPendingMember(false);
    } catch (error) {
      console.error("Failed to add user assignment:", error);
    } finally {
    }
  };

  const handleDeleteUserAssignmentInfo = async () => {
    try {
      await apiDeleteUserAssignmentInfo(id ?? "", targetRow.userId);
      store.dispatch(setShowAlert(t("memberDeletedSuccess")));
      await fetchAssignedUsers();
      await fetchPendingUsers();
      clearFields();
      setOpenDeleteMember(false);
    } catch (error) {
      console.error("Failed to delete memeber:", error);
    } finally {
    }
  };

  const handleDenyUserAssignmentInfo = async () => {
    try {
      if (!targetPendingRow) return;
      await apiDenyUserAssignmentInfo(id ?? "", targetPendingRow.userId);
      store.dispatch(setShowAlert(t("memberDenySuccess")));
      await fetchAssignedUsers();
      await fetchPendingUsers();
      clearFields();

      setOpenDenyMember(false);
    } catch (error) {
      console.error("Failed to deny memeber:", error);
    } finally {
    }
  };
  const { limit } = pageInfo;
  const fetchPendingUsers = useCallback(async () => {
    try {
      setIsLoadingPending(true);

      if (!id) return;

      const res = await apiPendingUsersWeb(
        id,
        page,
        limit,
        order,
        field,
        appliedSearch,
      );

      const { data, meta } = res.data;

      setRow(
        data.map((item: pendingUsers) => ({
          ...item,
          key: item.memberGroupInvitationId || item.userId,
        })),
      );

      setPageInfo({
        total: meta.pageInfo.total,
        page: meta.pageInfo.page,
        limit: meta.pageInfo.limit,
      });

      setPendingApprovals(meta.pageInfo.total);
    } catch (error) {
      console.error("Failed to fetch pending users:", error);
    } finally {
      setIsLoadingPending(false);
    }
  }, [id, page, limit, order, field, appliedSearch, setPendingApprovals]);

  const fetchAssignedUsers = useCallback(async () => {
    try {
      setIsLoadingAssigned(true);
      if (!id) return;

      const res = await apiAssignedUsersWeb(
        id,
        assignedPage,
        assignedPageInfo.limit,
        assignedOrder,
        assignedField,

        appliedSearch,
      );

      const { data, meta } = res.data;

      setAssignedRows(
        data.map((item: assignedUsers) => ({
          ...item,
          key: item.userId,
        })),
      );

      setAssignedPageInfo({
        total: meta.pageInfo.total,
        page: meta.pageInfo.page,
        limit: meta.pageInfo.limit,
      });
    } catch (error) {
      console.error("Failed to fetch pending users:", error);
    } finally {
      setIsLoadingAssigned(false);
    }
  }, [
    id,
    assignedPage,
    assignedOrder,
    assignedField,
    appliedSearch,
    assignedPageInfo.limit,
  ]);

  const normalize = (s: string) => (s || "").toString().trim().toLowerCase();

  const candidateFiltered = useMemo(() => {
    const q = normalize(memberQuery);
    if (!q) return candidateAll;

    const parts = q.split(/\s+/).filter(Boolean);

    return candidateAll.filter((u) => {
      const given = normalize(u.givenName ?? "");
      const sur = normalize(u.surname ?? "");

      const fullGS = `${given} ${sur}`.trim();
      const fullSG = `${sur} ${given}`.trim();
      const full = `${given} ${sur}`;

      if (
        fullGS.includes(q) ||
        fullSG.includes(q) ||
        given.includes(q) ||
        sur.includes(q)
      )
        return true;

      if (parts.length > 1 && parts.every((p) => full.includes(p))) return true;

      return false;
    });
  }, [candidateAll, memberQuery]);

  const isJa =
    typeof localeCode === "string"
      ? localeCode.toLowerCase().startsWith("ja")
      : false;

  const getGroupDetail = (id: string) => {
    return apiGroupDetail(id).then((e: { data: { data: any; meta: any } }) => {
      const { data, meta } = e.data;
      setTitleDetail(data);
      store.dispatch(saveScreenPermission(meta.screenPermission));
    });
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      store.dispatch(setShowAlert(t("linkCopied")));
    }
  };

  const buildMemberUrl = () => {
    const userId = userID ?? "";
    const groupId = id ?? "";
    const groupName = titleDetail?.groupName ?? "";
    const companyName = titleDetail?.companyName ?? "";
    const location = titleDetail?.location ?? "";

    let baseUrl = process.env.REACT_APP_ADD_MEMBER_BASE_URL ?? "";
    if (baseUrl.endsWith("/")) {
      baseUrl = baseUrl.slice(0, -1);
    }

    return `${baseUrl}/?groupName=${encodeURIComponent(
      groupName,
    )}&companyName=${encodeURIComponent(
      companyName,
    )}&location=${encodeURIComponent(location)}&groupId=${encodeURIComponent(
      groupId,
    )}&userId=${encodeURIComponent(userId)}`;
  };

  const handleGenerateLink: () => void = () => {
    const url = buildMemberUrl();
    setGeneratedLink(url);
    setOpenLinkDialog(true);
  };

  const handleGenerate2DCode: () => void = () => {
    const url = buildMemberUrl();
    setGeneratedLink(url);
    setOpen2DCodeDialog(true);
  };

  const handleDownloadQr = () => {
    const canvas = document.getElementById("qrCodeCanvas") as HTMLCanvasElement;
    if (canvas) {
      const pngUrl = canvas
        .toDataURL("image/png")
        .replace("image/png", "image/octet-stream");

      const groupName = titleDetail?.groupName ?? "group";
      const link = document.createElement("a");
      link.href = pngUrl;

      const now = new Date();

      let datePart = "";
      if (userTimeFormat.startsWith("dd/MM")) {
        const d = String(now.getDate()).padStart(2, "0");
        const m = String(now.getMonth() + 1).padStart(2, "0");
        const y = now.getFullYear();
        datePart = `${d}/${m}/${y}`;
      } else if (userTimeFormat.startsWith("MM/dd")) {
        const d = String(now.getDate()).padStart(2, "0");
        const m = String(now.getMonth() + 1).padStart(2, "0");
        const y = now.getFullYear();
        datePart = `${m}/${d}/${y}`;
      } else if (userTimeFormat.startsWith("yyyy")) {
        const d = String(now.getDate()).padStart(2, "0");
        const m = String(now.getMonth() + 1).padStart(2, "0");
        const y = now.getFullYear();
        datePart = `${y}/${m}/${d}`;
      }

      const h = String(now.getHours()).padStart(2, "0");
      const min = String(now.getMinutes()).padStart(2, "0");
      const s = String(now.getSeconds()).padStart(2, "0");
      const timePart = `${h}:${min}:${s}`;

      const formattedDateTime = `${datePart}_${timePart}`
        .replace(/\//g, "-")
        .replace(/:/g, "-");

      link.download = `${groupName}-${t("qr")}_${formattedDateTime}.png`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSearchMember = async (query: string) => {
    if (!id || !query.trim()) return;
    try {
      setCandidateSearchLoading(true);
      const res = await apiCandidateUsersSearchWeb(id, query);
      const { data } = res.data || {};
      setCandidateSearchAll(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch candidate users:", err);
      setCandidateSearchAll([]);
    } finally {
      setCandidateSearchLoading(false);
    }
  };

  useEffect(() => {
    const fetchMe = async () => {
      try {
        setIsLoading(true);
        const res = await getUser();
        const { data } = res.data;

        if (!data) {
          resetIsContinuousLoading();
          setIsLoading(false);
          return;
        }

        setLocaleCode(data.isoLocaleCode);
        setUserID(data.userId);
        setUserTimeFormat(data.timeFormat);
      } catch (error) {
        console.error("Failed to load Me:", error);
      } finally {
        resetIsContinuousLoading();
        setIsLoading(false);
      }
    };

    fetchMe();
  }, [id]);

  useEffect(() => {
    if (!Object.keys(screenPermission).length) {
      return;
    }
    if (!!screenPermission?.allowCreateEditDeleteMember) {
      setAllowCreateEditDeleteMember(
        screenPermission.allowCreateEditDeleteMember,
      );
    } else {
      window.location.href = `/groups/${id}`;
    }
  }, [screenPermission, id]);

  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  useEffect(() => {
    fetchAssignedUsers();
  }, [fetchAssignedUsers]);

  useEffect(() => {
    if (searchText === "" && appliedSearch !== "") {
      setAppliedSearch("");
      setPage(1);
      setMemberSearch("");
    }
  }, [searchText, appliedSearch, setMemberSearch]);

  useEffect(() => {
    if (assignedSearchText === "" && assignedAppliedSearch !== "") {
      setAssignedAppliedSearch("");
      setAssignedPage(1);
    }
  }, [assignedSearchText, assignedAppliedSearch]);

  useEffect(() => {
    if (!openAddMember || !id) return;

    let cancelled = false;
    (async () => {
      try {
        setCandidateLoading(true);
        const res = await apiCandidateUsersWeb(id);
        const { data } = res.data || {};
        if (!cancelled) {
          setCandidateAll(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch candidate users:", err);
        if (!cancelled) setCandidateAll([]);
      } finally {
        if (!cancelled) setCandidateLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openAddMember, id]);

  useEffect(() => {
    getGroupDetail(`${id}`).finally(() => {
      dispatch(setIsRequestedGroupDetail(true));
    });
  }, [id, dispatch]);

  useEffect(() => {
    if (!openAddSearchMember || !id) return;
  }, [openAddSearchMember, id]);

  const normalizeUniversal = (s: string) => {
    if (!s) return "";

    return s
      .normalize("NFKC")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "")
      .replace(/[\u3041-\u3096]/g, (m) =>
        String.fromCharCode(m.charCodeAt(0) + 0x60),
      );
  };

  const filterByNameAndRole = useCallback(
    (items: any[]) => {
      const q = normalizeUniversal(searchText);
      if (!q) return items;

      return items.filter((u) => {
        const rawName = u.reporterName || "";
        const rawRole = u.roleName || "";

        const parts = rawName.trim().split(/\s+/);
        const normalizedParts = parts.map((p: string) => normalizeUniversal(p));

        const fullNameNoSpaces = normalizeUniversal(parts.join(""));
        const roleNorm = normalizeUniversal(rawRole);

        return (
          normalizedParts.some((p: string | string[]) => p.includes(q)) ||
          fullNameNoSpaces.includes(q) ||
          roleNorm.includes(q)
        );
      });
    },
    [searchText],
  );

  const filteredPendingRows = useMemo(() => rows ?? [], [rows]);

  const filteredAssignedRows = useMemo(
    () => filterByNameAndRole(assignedRows ?? []),
    [assignedRows, filterByNameAndRole],
  );

  const isGroupManagerSelected = useMemo(() => {
    const role = templates.find(
      (t) => t.userGroupRoleTemplateId === selectedRole,
    );
    return role?.roleCode === "role code 1";
  }, [selectedRole, templates]);

  const hasAnyPermission =
    isGroupManagerSelected || permissions.some((p) => p.isChecked);

  return allowCreateEditDeleteMember ? (
    <div className="container" style={{ marginTop: "25px" }}>
      <div className="row" style={{ marginBottom: "25px" }}>
        <div className="mb-16 sp-p24">
          <div
            className="d-flex"
            style={{
              flexDirection: isSmallScreen ? "column" : "row",
              justifyContent:
                rows && rows.length > 0 ? "space-between" : "flex-end",
              alignItems: isSmallScreen ? "flex-start" : "flex-end",
              marginTop: "0px",
              gap: "10px",
            }}
          >
            {rows && rows.length > 0 ? (
              <div className="d-flex flex-row align-items-center gap-2">
                <Typography>
                  {t("pendingApprovals")}
                  {rows && rows.length > 0 && (
                    <span
                      style={{
                        display: "inline-flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minWidth: "17px",
                        height: "17px",
                        padding: "0 5.5px",
                        borderRadius: "50%",
                        backgroundColor: "#140a9a",
                        color: "#fff",
                        fontSize: "11px",
                        fontWeight: "600",
                        marginInlineStart: "6px",
                      }}
                    >
                      {pageInfo.total}
                    </span>
                  )}
                </Typography>
              </div>
            ) : null}
          </div>
        </div>

        {rows && rows.length > 0 ? (
          <>
            <DataTable
              rowKey="userId"
              columns={pendingColumns}
              dataSource={filteredPendingRows}
              onRequestSort={handleSort}
              textEmptyData="noUsers"
              loading={isLoading || isLoadingPending}
              skeletonRows={2}
            />
          </>
        ) : null}
      </div>

      <div
        className="row"
        style={{
          marginTop: rows && rows.length > 0 ? "0px" : "0px",
        }}
      >
        <div className="mb-16 sp-p24">
          <div
            className="d-flex"
            style={{
              flexDirection: "column",
              marginTop: "10px",
              gap: "10px",
            }}
          >
            <div
              className="d-flex"
              style={{
                justifyContent: "space-between",
                alignItems: isSmallScreen ? "flex-start" : "flex-end",
              }}
            >
              <div className="d-flex flex-row align-items-center gap-2">
                <Typography>{t("assignedUsers")}</Typography>
              </div>

              <div style={{ position: "relative" }}>
                <InputText
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={t("Customermachinenumber")}
                  className="custom-input"
                  style={{
                    width: "277px",
                    paddingLeft: "1rem",
                    paddingRight: "2rem",
                    height: "50px",
                    border: "1.5px solid #bcbcbc",
                    borderRadius: "30px",
                    boxSizing: "border-box",
                  }}
                />

                {searchText && (
                  <button
                    className="clear-button"
                    onClick={() => setSearchText("")}
                    aria-label="Clear search"
                    type="button"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                className="btn btn-primary"
                startIcon={<IconPlus />}
                variant="contained"
                onClick={() => setOpenAddMember(true)}
                style={{
                  height: "45px",
                  fontSize: "14px",
                  marginRight: isSmallScreen ? "0px" : "10px",
                }}
              >
                {t("addMemberInfo")}
              </Button>
            </div>
          </div>
        </div>

        <DataTable
          rowKey="userId"
          columns={assignedColumns}
          dataSource={filteredAssignedRows}
          onRequestSort={handleAssignedSort}
          textEmptyData="noUsers"
          loading={isLoading || isLoadingAssigned}
          skeletonRows={3}
        />

        <Menu
          anchorEl={anchorElAssigned}
          open={openAssigned}
          onClose={() => setAnchorElAssigned(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              setAnchorElAssigned(null);
              handleOpenEdit();
            }}
          >
            <ListItemIcon>
              <IconEdit />
            </ListItemIcon>
            <ListItemText primary={t("editMemberInfo")} />
          </MenuItem>

          <MenuItem
            onClick={() => {
              setAnchorElAssigned(null);
              setOpenDeleteMember(true);
            }}
          >
            <ListItemIcon>
              <IconDelete />
            </ListItemIcon>
            <ListItemText
              primary={t("formAction.delete")}
              primaryTypographyProps={{ style: { color: "#DA093B" } }}
            />
          </MenuItem>
        </Menu>
      </div>

      <DataDialog
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{t("updateMemberInfo")}</span>
            {!selectedRole && (
              <span style={{ color: "red", fontSize: "12px", fontWeight: 500 }}>
                {t("required")}*
              </span>
            )}
          </div>
        }
        open={openUpdateMember}
        setOpen={setOpenUpdateMember}
        disableBackdropClick={true}
      >
        <TextField
          label={t("name")}
          className="input-field"
          value={targetRow?.reporterName || ""}
          fullWidth
          margin="normal"
          disabled
        />

        {!loadingTemplates && (
          <TextField
            select
            required
            label={t("role")}
            value={selectedRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            fullWidth
            margin="normal"
          >
            {templates.map((role) => (
              <MenuItem
                key={role.userGroupRoleTemplateId}
                value={role.userGroupRoleTemplateId}
              >
                {role.userGroupRoleNameTranslation?.roleName}
              </MenuItem>
            ))}
          </TextField>
        )}

        {(() => {
          const selected = templates.find(
            (r) => r.userGroupRoleTemplateId === selectedRole,
          );
          if (selected && selected.roleCode !== "role code 1") {
            return (
              <TextField
                className="input-field"
                label={t("customizeRoleName")}
                value={extraRoleName}
                onChange={(e) => setExtraRoleName(e.target.value)}
                fullWidth
                margin="normal"
                helperText={t("customizeRoleNameHelper")}
              />
            );
          }
          return null;
        })()}

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, fontWeight: "normal", color: "black" }}
        >
          {t("permission")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          {permissions.map((perm) => (
            <FormControlLabel
              key={perm.permissionId}
              control={
                <Checkbox
                  checked={isGroupManagerSelected ? true : perm.isChecked}
                  onChange={() => togglePermission(perm.permissionId)}
                  disabled={isGroupManagerSelected || perm.disabled}
                  icon={
                    (isGroupManagerSelected || perm.disabled) &&
                    !perm.isChecked ? (
                      <CheckBoxOutlineBlankIcon
                        sx={{
                          color: "#9e9e9e",
                          backgroundColor: "#9e9e9e",
                          borderRadius: "2px",
                          fontSize: 18,
                          marginLeft: "3px",
                        }}
                      />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )
                  }
                  checkedIcon={
                    isGroupManagerSelected ? (
                      <CheckBoxIcon sx={{ color: "#9e9e9e" }} />
                    ) : perm.disabled ? (
                      <CheckBoxIcon sx={{ color: "#9e9e9e" }} />
                    ) : (
                      <CheckBoxIcon sx={{ color: "#140a9a" }} />
                    )
                  }
                />
              }
              label={perm.permissionName}
            />
          ))}
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button onClick={clearFields} className="btn btn-no-border">
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn btn-primary"
            disabled={!selectedRole || !hasAnyPermission}
            onClick={() => {
              handleUpdateUserAssignmentInfo();
            }}
          >
            {t("formAction.update")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("deleteMember")}
        open={openDeleteMember}
        setOpen={setOpenDeleteMember}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("deleteMemberMessage")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                clearFields();
                setOpenDeleteMember(false);
              }}
              className="btn btn-no-border"
              style={{ marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn btn-primary bg-red"
              onClick={() => {
                handleDeleteUserAssignmentInfo();
              }}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{t("addMemberInfo")}</span>
            {!selectedRole && (
              <span style={{ color: "red", fontSize: "12px", fontWeight: 500 }}>
                {t("required")}*
              </span>
            )}
          </div>
        }
        open={openAddPendingMember}
        setOpen={setOpenAddPendingMember}
        disableBackdropClick={true}
      >
        <TextField
          label={t("name")}
          className="input-field"
          value={targetPendingRow?.reporterName || ""}
          fullWidth
          margin="normal"
          disabled
        />

        {!loadingTemplates && (
          <TextField
            select
            required
            label={t("role")}
            value={selectedRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            fullWidth
            margin="normal"
            error={!selectedRole}
          >
            {templates.map((role) => (
              <MenuItem
                key={role.userGroupRoleTemplateId}
                value={role.userGroupRoleTemplateId}
              >
                {role.userGroupRoleNameTranslation?.roleName}
              </MenuItem>
            ))}
          </TextField>
        )}

        {(() => {
          const selected = templates.find(
            (r) => r.userGroupRoleTemplateId === selectedRole,
          );
          if (selected && selected.roleCode !== "role code 1") {
            return (
              <TextField
                className="input-field"
                label={t("customizeRoleName")}
                value={extraRoleName}
                onChange={(e) => setExtraRoleName(e.target.value)}
                fullWidth
                margin="normal"
                helperText={t("customizeRoleNameHelper")}
              />
            );
          }
          return null;
        })()}

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, fontWeight: "normal", color: "black" }}
        >
          {t("permission")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          {permissions.map((perm) => (
            <FormControlLabel
              key={perm.permissionId}
              control={
                <Checkbox
                  checked={perm.isChecked}
                  onChange={() => togglePermission(perm.permissionId)}
                  disabled={perm.disabled}
                  icon={
                    perm.disabled && !perm.isChecked ? (
                      <CheckBoxOutlineBlankIcon
                        sx={{
                          color: "#9e9e9e",
                          backgroundColor: "#9e9e9e",
                          borderRadius: "2px",
                          fontSize: 18,
                          marginLeft: "3px",
                        }}
                      />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )
                  }
                  checkedIcon={
                    perm.disabled ? (
                      <CheckBoxIcon sx={{ color: "#9e9e9e" }} />
                    ) : (
                      <CheckBoxIcon sx={{ color: "#140a9a" }} />
                    )
                  }
                />
              }
              label={perm.permissionName}
            />
          ))}
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button onClick={clearFields} className="btn btn-no-border">
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn-primary bg-red"
            sx={{
              borderRadius: "20px",
              color: "#fff",
              textTransform: "none",
            }}
            onClick={() => {
              setOpenDenyMember(true);
            }}
          >
            {t("formDelete.deny")}
          </Button>

          <Button
            disabled={!selectedRole || !hasAnyPermission}
            className="btn btn-primary"
            onClick={() => {
              handleAddPendingUserAssignmentInfo();
            }}
          >
            {t("formAction.add")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        disableBackdropClick={true}
        title={t("denyMember")}
        open={openDenyMember}
        setOpen={setOpenDenyMember}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("denyMemberMessage")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => {
                setOpenDenyMember(false);
              }}
              className="btn btn-no-border"
              style={{ marginRight: "10px" }}
            >
              {t("dialogReport.cancel")}
            </Button>

            <Button
              className="btn-primary bg-red"
              sx={{
                borderRadius: "20px",
                color: "#fff",
                textTransform: "none",
              }}
              onClick={() => {
                handleDenyUserAssignmentInfo();
              }}
            >
              {t("formDelete.deny")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <DataDialog
        title={t("addMemberInfo")}
        open={openAddMember}
        setOpen={setOpenAddMember}
        disableBackdropClick={true}
      >
        <Box
          sx={{ p: 2, height: 500, display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "nowrap",
              overflowX: "auto",
              mb: 3,
              pb: 1,
            }}
          >
            <Button
              onClick={() => setOpenAddSearchMember(true)}
              variant="text"
              disableElevation
              sx={{
                width: 148,
                height: 110,
                borderRadius: 4,
                backgroundColor: "rgba(0,0,0,0.04)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
                textTransform: "none",
                p: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <SearchOutlinedIcon
                  sx={{ fontSize: 30, color: "#000", mb: 1 }}
                />
                <Typography sx={{ fontSize: 16, color: "#000" }}>
                  {t("searchMember")}
                </Typography>
              </Box>
            </Button>

            <Button
              variant="text"
              disableElevation
              sx={{
                width: 148,
                height: 110,
                borderRadius: 4,
                backgroundColor: "rgba(0,0,0,0.04)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
                textTransform: "none",
                p: 0,
              }}
              onClick={handleGenerate2DCode}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <QrCode2OutlinedIcon
                  sx={{ fontSize: 30, color: "#000", mb: 1 }}
                />
                <Typography sx={{ fontSize: 16, color: "#000" }}>
                  {t("formAction.2DCode")}
                </Typography>
              </Box>
            </Button>

            <Button
              variant="text"
              disableElevation
              sx={{
                width: 148,
                height: 110,
                borderRadius: 4,
                backgroundColor: "rgba(0,0,0,0.04)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.06)" },
                textTransform: "none",
                p: 0,
              }}
              onClick={handleGenerateLink}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <LinkOutlinedIcon sx={{ fontSize: 30, color: "#000", mb: 1 }} />
                <Typography sx={{ fontSize: 16, color: "#000" }}>
                  {t("link")}
                </Typography>
              </Box>
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography
            variant="subtitle1"
            sx={{ mt: 2, fontWeight: "normal", color: "black" }}
          >
            {t("relatedMembers")}
          </Typography>

          <TextField
            className="input-field"
            fullWidth
            placeholder={t("enterUserName")}
            value={memberQuery}
            onChange={(e) => setMemberQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ mt: 2, flex: 1, overflowY: "auto" }}>
            {addMode === "search" && (
              <>
                {!candidateLoading && candidateAll.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 3,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconBox />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      {t("noMembersAvailable")}
                    </Typography>
                  </Box>
                ) : candidateFiltered.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 3,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconBox />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                    >
                      {t("noMembersAvailable")}
                    </Typography>
                  </Box>
                ) : (
                  <List dense sx={{ width: "100%" }}>
                    {candidateFiltered.map((u) => {
                      const primaryName = (
                        isJa
                          ? `${u.surname ?? ""} ${u.givenName ?? ""}`
                          : `${u.givenName ?? ""} ${u.surname ?? ""}`
                      ).trim();

                      return (
                        <ListItem
                          key={u.userId}
                          disableGutters
                          tabIndex={0}
                          sx={{
                            borderBottom: "1px solid #eee",
                            py: 1,
                            cursor: "pointer",
                            outline: "none",
                            "&:focus": {
                              border: "2px solid #0d47a1",
                              borderRadius: "4px",
                            },
                          }}
                          onClick={(e) => {
                            (e.currentTarget as HTMLElement).blur();
                            setTargetCandidateRow({ ...u });
                            fetchTemplates();
                            setOpenAddNewMember(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              (e.currentTarget as HTMLElement).blur();
                              setTargetCandidateRow({ ...u });
                              fetchTemplates();
                              setOpenAddNewMember(true);
                            }
                          }}
                        >
                          <ListItemAvatar sx={{ marginLeft: "10px" }}>
                            {u.pictureUrl ? (
                              <Avatar
                                src={u.pictureUrl}
                                sx={{ width: 36, height: 36 }}
                              />
                            ) : (
                              <Avatar sx={{ width: 36, height: 36 }}>
                                <IconUser />
                              </Avatar>
                            )}
                          </ListItemAvatar>

                          <ListItemText
                            primary={primaryName}
                            primaryTypographyProps={{
                              sx: { fontWeight: "normal", color: "#000" },
                            }}
                            secondaryTypographyProps={{
                              sx: { color: "text.secondary", fontSize: 14 },
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </>
            )}
          </Box>
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            className="btn btn-primary"
            onClick={() => {
              clearFields();
            }}
          >
            {t("dialogRefuel.close")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{t("addMemberInfo")}</span>
            {!selectedRole && (
              <span style={{ color: "red", fontSize: "12px", fontWeight: 500 }}>
                {t("required")}*
              </span>
            )}
          </div>
        }
        open={openAddNewMember}
        setOpen={setOpenAddNewMember}
        disableBackdropClick={true}
      >
        <TextField
          label={t("name")}
          className="input-field"
          value={
            targetCandidateRow
              ? isJa
                ? `${targetCandidateRow.surname ?? ""} ${
                    targetCandidateRow.givenName ?? ""
                  }`
                : `${targetCandidateRow.givenName ?? ""} ${
                    targetCandidateRow.surname ?? ""
                  }`
              : ""
          }
          fullWidth
          margin="normal"
          disabled
        />

        {!loadingTemplates && (
          <TextField
            select
            required
            label={t("role")}
            value={selectedRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            fullWidth
            margin="normal"
            error={!selectedRole}
          >
            {templates.map((role) => (
              <MenuItem
                key={role.userGroupRoleTemplateId}
                value={role.userGroupRoleTemplateId}
              >
                {role.userGroupRoleNameTranslation?.roleName}
              </MenuItem>
            ))}
          </TextField>
        )}

        {(() => {
          const selected = templates.find(
            (r) => r.userGroupRoleTemplateId === selectedRole,
          );
          if (selected && selected.roleCode !== "role code 1") {
            return (
              <TextField
                className="input-field"
                label={t("customizeRoleName")}
                value={extraRoleName}
                onChange={(e) => setExtraRoleName(e.target.value)}
                fullWidth
                margin="normal"
                helperText={t("customizeRoleNameHelper")}
              />
            );
          }
          return null;
        })()}

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, fontWeight: "normal", color: "black" }}
        >
          {t("permission")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          {permissions.map((perm) => (
            <FormControlLabel
              key={perm.permissionId}
              control={
                <Checkbox
                  checked={perm.isChecked}
                  onChange={() => togglePermission(perm.permissionId)}
                  disabled={perm.disabled}
                  icon={
                    perm.disabled && !perm.isChecked ? (
                      <CheckBoxOutlineBlankIcon
                        sx={{
                          color: "#9e9e9e",
                          backgroundColor: "#9e9e9e",
                          borderRadius: "2px",
                          fontSize: 18,
                          marginLeft: "3px",
                        }}
                      />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )
                  }
                  checkedIcon={
                    perm.disabled ? (
                      <CheckBoxIcon sx={{ color: "#9e9e9e" }} />
                    ) : (
                      <CheckBoxIcon sx={{ color: "#140a9a" }} />
                    )
                  }
                />
              }
              label={perm.permissionName}
            />
          ))}
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button onClick={clearFieldsAddMember} className="btn btn-no-border">
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            disabled={!selectedRole || !hasAnyPermission}
            onClick={() => {
              handleAddUserAssignmentInfo();
            }}
          >
            {t("formAction.add")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        title={t("linkCreated")}
        open={openLinkDialog}
        setOpen={setOpenLinkDialog}
        disableBackdropClick={true}
      >
        <Box sx={{ marginLeft: 1, marginRight: 1 }}>
          <TextField
            className="input-field"
            fullWidth
            size="small"
            value={generatedLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCopyLink}
                    sx={{
                      textTransform: "none",
                      borderColor: "#140a9a",
                      color: "#140a9a",
                      "&:hover": {
                        borderColor: "#140a9a",
                        backgroundColor: "#140a9a",
                        color: "#fff",
                      },
                      borderRadius: "6px",
                      px: 0,
                      fontWeight: 500,
                      minWidth: 70,
                      marginLeft: 0,
                      marginRight: 0,
                    }}
                  >
                    {t("formAction.copy")}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            className="btn btn-primary"
            onClick={() => {
              setOpenLinkDialog(false);
              setGeneratedLink("");
            }}
          >
            {t("dialogRefuel.close")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        title={t("scan2DCode")}
        open={open2DCodeDialog}
        setOpen={setOpen2DCodeDialog}
        disableBackdropClick={true}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 3,
          }}
        >
          {generatedLink && (
            <>
              <QRCodeCanvas
                id="qrCodeCanvas"
                value={generatedLink}
                size={280}
                fgColor="#000000"
                bgColor="#ffffff"
                level="H"
                includeMargin={true}
              />

              <IconButton onClick={handleDownloadQr} sx={{ mt: 2 }}>
                <DownloadIcon />
              </IconButton>
            </>
          )}
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            className="btn btn-primary"
            onClick={() => {
              setOpen2DCodeDialog(false);
              setGeneratedLink("");
            }}
          >
            {t("dialogRefuel.close")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        title={t("searchMember")}
        open={openAddSearchMember}
        setOpen={setOpenAddSearchMember}
        disableBackdropClick={true}
      >
        <Box sx={{ p: 2 }}>
          <TextField
            className="input-field"
            fullWidth
            placeholder={t("enterUserNameUniversal")}
            value={searchTextMember}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTextMember(value);

              if (value === "") {
                setCandidateSearchAll([]);
                setCandidateSearchLoading(false);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearchMember(searchTextMember);
              }
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ mt: 2 }}>
            {!candidateSearchLoading && candidateSearchAll.length > 0 && (
              <List dense sx={{ width: "100%" }}>
                {candidateSearchAll.map((u) => {
                  const primaryName = (
                    isJa
                      ? `${u.surname ?? ""} ${u.givenName ?? ""}`
                      : `${u.givenName ?? ""} ${u.surname ?? ""}`
                  ).trim();

                  return (
                    <ListItem
                      key={u.userId}
                      disableGutters
                      tabIndex={u.isAlreadyInGroup ? -1 : 0}
                      sx={{
                        borderBottom: "1px solid #eee",
                        py: 1,
                        cursor: u.isAlreadyInGroup ? "not-allowed" : "pointer",
                        outline: "none",
                        opacity: u.isAlreadyInGroup ? 0.5 : 1,
                        ...(!u.isAlreadyInGroup && {
                          "&:focus": {
                            border: "2px solid #0d47a1",
                            borderRadius: "4px",
                          },
                        }),
                      }}
                      onClick={(e) => {
                        if (u.isAlreadyInGroup) return;
                        (e.currentTarget as HTMLElement).blur();
                        setTargetCandidateSearchRow({ ...u });
                        fetchTemplates();
                        setOpenAddNewMemberSearch(true);
                      }}
                      onKeyDown={(e) => {
                        if (u.isAlreadyInGroup) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          (e.currentTarget as HTMLElement).blur();
                          setTargetCandidateSearchRow({ ...u });
                          fetchTemplates();
                          setOpenAddNewMemberSearch(true);
                        }
                      }}
                    >
                      <ListItemAvatar sx={{ marginLeft: "10px" }}>
                        {u.pictureUrl ? (
                          <Avatar
                            src={u.pictureUrl}
                            sx={{ width: 36, height: 36 }}
                          />
                        ) : (
                          <Avatar sx={{ width: 36, height: 36 }}>
                            <IconUser />
                          </Avatar>
                        )}
                      </ListItemAvatar>

                      <ListItemText
                        primary={primaryName}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: "normal",
                            color: u.isAlreadyInGroup
                              ? "text.disabled"
                              : "#000",
                          },
                        }}
                        secondary={
                          u.isAlreadyInGroup ? t("alreadyInGroup") : undefined
                        }
                        secondaryTypographyProps={{
                          sx: { color: "text.secondary", fontSize: 14 },
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            className="btn btn-primary"
            onClick={() => {
              clearFieldsAddMember();
            }}
          >
            {t("dialogRefuel.close")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{t("addMemberInfo")}</span>
            {!selectedRole && (
              <span style={{ color: "red", fontSize: "12px", fontWeight: 500 }}>
                {t("required")}*
              </span>
            )}
          </div>
        }
        open={openAddNewMemberSearch}
        setOpen={setOpenAddNewMemberSearch}
        disableBackdropClick={true}
      >
        <TextField
          label={t("name")}
          className="input-field"
          value={
            targetCandidateSearchRow
              ? isJa
                ? `${targetCandidateSearchRow.surname ?? ""} ${
                    targetCandidateSearchRow.givenName ?? ""
                  }`
                : `${targetCandidateSearchRow.givenName ?? ""} ${
                    targetCandidateSearchRow.surname ?? ""
                  }`
              : ""
          }
          fullWidth
          margin="normal"
          disabled
        />

        {!loadingTemplates && (
          <TextField
            select
            required
            label={t("role")}
            value={selectedRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            fullWidth
            margin="normal"
            error={!selectedRole}
          >
            {templates.map((role) => (
              <MenuItem
                key={role.userGroupRoleTemplateId}
                value={role.userGroupRoleTemplateId}
              >
                {role.userGroupRoleNameTranslation?.roleName}
              </MenuItem>
            ))}
          </TextField>
        )}

        {(() => {
          const selected = templates.find(
            (r) => r.userGroupRoleTemplateId === selectedRole,
          );
          if (selected && selected.roleCode !== "role code 1") {
            return (
              <TextField
                className="input-field"
                label={t("customizeRoleName")}
                value={extraRoleName}
                onChange={(e) => setExtraRoleName(e.target.value)}
                fullWidth
                margin="normal"
                helperText={t("customizeRoleNameHelper")}
              />
            );
          }
          return null;
        })()}

        <Typography
          variant="subtitle1"
          sx={{ mt: 2, fontWeight: "normal", color: "black" }}
        >
          {t("permission")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          {permissions.map((perm) => (
            <FormControlLabel
              key={perm.permissionId}
              control={
                <Checkbox
                  checked={perm.isChecked}
                  onChange={() => togglePermission(perm.permissionId)}
                  disabled={perm.disabled}
                  icon={
                    perm.disabled && !perm.isChecked ? (
                      <CheckBoxOutlineBlankIcon
                        sx={{
                          color: "#9e9e9e",
                          backgroundColor: "#9e9e9e",
                          borderRadius: "2px",
                          fontSize: 18,
                          marginLeft: "3px",
                        }}
                      />
                    ) : (
                      <CheckBoxOutlineBlankIcon />
                    )
                  }
                  checkedIcon={
                    perm.disabled ? (
                      <CheckBoxIcon sx={{ color: "#9e9e9e" }} />
                    ) : (
                      <CheckBoxIcon sx={{ color: "#140a9a" }} />
                    )
                  }
                />
              }
              label={perm.permissionName}
            />
          ))}
        </Box>

        <div
          className="flex justify-content-end btn-group-footer mt-8"
          style={{
            backgroundColor: "#fff",
            paddingTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            onClick={clearFieldsAddMemberSearch}
            className="btn btn-no-border"
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            disabled={!selectedRole || !hasAnyPermission}
            onClick={() => {
              handleAddSearchUserAssignmentInfo();
            }}
          >
            {t("formAction.add")}
          </Button>
        </div>
      </DataDialog>
    </div>
  ) : null;
}
